import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Check, Loader2, MessageSquareText, Sparkles, X } from 'lucide-react'
import { cn } from '@lib/utils'
import { useToast } from '@hooks/use-toast'
import { useClinicFormat } from '@context/ClinicTimezoneContext'
import { appointmentApi } from '@services/appointment.service'
import type { Appointment, Note } from '@/types'
import { doctorLabel, isScarlettAuthor, patientLabel } from '@hooks/useScarlettInbox'
import { useAuthStore } from '@hooks/useAuthStore'
import { canDecideAppointment } from '@lib/appointmentPermissions'

interface ScarlettPanelProps {
  pending: Appointment[]
  notes: Note[]
  isLoading: boolean
  pendingLoading?: boolean
  notesLoading?: boolean
  pendingError?: boolean
  notesError?: boolean
  onRetryPending?: () => void
  onRetryNotes?: () => void
}

const relative = (iso: string) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (m < 1) return 'ahora mismo'
  if (m < 60) return `hace ${m} min`
  const h = Math.floor(m / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.floor(h / 24)
  return d === 1 ? 'ayer' : `hace ${d} días`
}

export function ScarlettPanel({
  pending,
  notes,
  isLoading,
  pendingLoading,
  notesLoading,
  pendingError = false,
  notesError = false,
  onRetryPending,
  onRetryNotes,
}: ScarlettPanelProps) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { toast } = useToast()
  const { formatDateTime } = useClinicFormat()
  const user = useAuthStore((state) => state.user)
  const isPendingLoading = pendingLoading ?? isLoading
  const isNotesLoading = notesLoading ?? isLoading

  const done = () => { void qc.invalidateQueries() }
  const approve = useMutation({
    mutationFn: (id: string) => appointmentApi.approveAppointment(id),
    onSuccess: () => { toast({ title: 'Cita aprobada', description: 'La cita quedó programada. Falta confirmar con el paciente.' }); done() },
    onError: () => toast({ title: 'No se pudo aprobar', variant: 'destructive' }),
  })
  const reject = useMutation({
    mutationFn: (id: string) => appointmentApi.rejectAppointment(id, { rejection_reason: 'Rechazada desde Inicio' }),
    onSuccess: () => { toast({ title: 'Solicitud rechazada' }); done() },
    onError: () => toast({ title: 'No se pudo rechazar', variant: 'destructive' }),
  })
  const busy = (id: string) => (approve.isPending && approve.variables === id) || (reject.isPending && reject.variables === id)

  return (
    <section className="flex min-w-0 flex-col rounded-2xl border border-ml-outline-variant/15 bg-ml-surface-container-lowest">
      <header className="flex items-center gap-3 border-b border-ml-outline-variant/10 px-5 py-4 sm:px-6">
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-ml-primary text-ml-on-primary"><Sparkles className="h-4 w-4" /></span>
        <div className="min-w-0">
          <h2 className="font-headline text-2xl leading-tight text-ml-primary">Recepción por WhatsApp</h2>
          <p className="text-xs text-ml-on-surface-variant/70">Lo que Scarlett atendió y necesita tu ojo</p>
        </div>
      </header>

      {/* Solicitudes por confirmar */}
      <div className="border-b border-ml-outline-variant/10 px-5 py-4 sm:px-6">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.16em] text-ml-on-surface-variant/65">Solicitudes por aprobar</h3>
          {!pendingError && !isPendingLoading && pending.length > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800 tabular-nums">{pending.length}</span>}
        </div>
        {pendingError ? (
          <div role="alert" className="rounded-xl border border-ml-error/20 bg-ml-error/5 px-4 py-4 text-sm text-ml-on-surface-variant">
            <p>No se pudieron cargar las solicitudes pendientes.</p>
            <button type="button" onClick={onRetryPending} className="mt-2 font-semibold text-ml-primary hover:underline">Reintentar solicitudes</button>
          </div>
        ) : isPendingLoading ? (
          <div className="h-16 animate-pulse rounded-xl bg-ml-surface-container-low" />
        ) : pending.length === 0 ? (
          <p className="rounded-xl border border-dashed border-ml-outline-variant/30 px-4 py-4 text-sm text-ml-on-surface-variant/70">No hay solicitudes pendientes de aprobación.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {pending.slice(0, 5).map((a) => (
              <li key={a.id} className="flex flex-col gap-3 rounded-xl border border-amber-200/70 bg-amber-50/50 p-3 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ml-on-surface">{patientLabel(a)}</p>
                  <p className="truncate text-xs text-ml-on-surface-variant/80">
                    {formatDateTime(a.appointment_date)} · {a.reason || a.appointment_type}{doctorLabel(a) ? ` · ${doctorLabel(a)}` : ''}
                  </p>
                </div>
                {canDecideAppointment(user, a) ? <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => approve.mutate(a.id)} disabled={busy(a.id)}
                    className="inline-flex h-9 flex-1 items-center justify-center gap-1 rounded-lg bg-ml-primary px-3 text-xs font-semibold text-ml-on-primary hover:bg-ml-primary-container disabled:opacity-60 sm:flex-none"
                  >
                    {busy(a.id) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Aceptar
                  </button>
                  <button
                    onClick={() => reject.mutate(a.id)} disabled={busy(a.id)}
                    className="inline-flex h-9 flex-1 items-center justify-center gap-1 rounded-lg border border-ml-outline-variant/30 bg-white px-3 text-xs font-semibold text-ml-on-surface hover:bg-ml-surface-container-low disabled:opacity-60 sm:flex-none"
                  >
                    <X className="h-3.5 w-3.5" /> Rechazar
                  </button>
                </div> : <p className="text-xs text-ml-on-surface-variant">Pendiente de aprobación del médico</p>}
              </li>
            ))}
            {pending.length > 5 && (
              <button onClick={() => navigate('/agenda')} className="text-left text-xs font-semibold text-ml-primary underline-offset-4 hover:underline">Ver las {pending.length} en la agenda →</button>
            )}
          </ul>
        )}
      </div>

      {/* Notas de recepción */}
      <div className="px-5 py-4 sm:px-6">
        <h3 className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-ml-on-surface-variant/65">Últimas notas de recepción</h3>
        {notesError ? (
          <div role="alert" className="rounded-xl border border-ml-error/20 bg-ml-error/5 px-4 py-4 text-sm text-ml-on-surface-variant">
            <p>No se pudieron cargar las notas de recepción.</p>
            <button type="button" onClick={onRetryNotes} className="mt-2 font-semibold text-ml-primary hover:underline">Reintentar notas</button>
          </div>
        ) : isNotesLoading ? (
          <div className="h-24 animate-pulse rounded-xl bg-ml-surface-container-low" />
        ) : notes.length === 0 ? (
          <p className="rounded-xl border border-dashed border-ml-outline-variant/30 px-4 py-4 text-sm text-ml-on-surface-variant/70">Aún no hay notas. Scarlett deja aquí lo que cada paciente le cuenta: molestia, tratamiento de interés, preferencias y pendientes.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-ml-outline-variant/10">
            {notes.slice(0, 5).map((n) => (
              <li key={n.id}>
                <button
                  onClick={() => navigate(`/workspace/patient/${n.patient_id}/notas`)}
                  className="group flex w-full flex-col gap-1.5 py-3 text-left transition hover:bg-ml-surface-container-low/60 sm:rounded-lg sm:px-2"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                      isScarlettAuthor(n.author_name) ? 'border-ml-primary/15 bg-ml-primary/10 text-ml-primary' : 'border-ml-outline-variant/30 bg-ml-surface-container-low text-ml-on-surface-variant',
                    )}>
                      <MessageSquareText className="h-3 w-3" /> {isScarlettAuthor(n.author_name) ? 'WhatsApp · Scarlett' : n.author_name}
                    </span>
                    <span className="text-[11px] text-ml-on-surface-variant/60">{relative(n.created_at)}</span>
                  </div>
                  <p className="text-sm font-semibold text-ml-on-surface group-hover:text-ml-primary">{n.title}</p>
                  <p className="line-clamp-2 text-sm leading-snug text-ml-on-surface-variant">{n.content}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
